import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { usePrevious } from 'react-use';
import { useDispatch } from 'react-redux';

import {
  INITIAL_GEOLOCATION_MAP_ZOOM,
  LOCATION_SEARCH_PARAM_NAME,
} from '../../../../common/utils/constants/constants';
import { useGlMap } from '../../../contexts/context';
import { useMobile } from '../../../../common/components/responsive/Responsive';
import Icon from '../../../../common/components/icon/Icon';
import Overlay from '../../../../common/components/overlay/Overlay';
import Button, {
  appearances,
} from '../../../../common/components/controls/button/Button';
import CloseButton from '../../../../common/components/close-button/CloseButton';
import { geolocationChanged } from '../../../store';

import './InitialLoadGeolocationControl.scss';

export const GEOLOCATION_PERMISSION_DENIED_ERROR_CODE = 1;
export const GEOLOCATION_POSITION_UNAVAILABLE_ERROR_CODE = 2;
export const GEOLOCATION_TIMEOUT_ERROR_CODE = 3;

class GeolocationError extends Error {
  constructor(message: string, options: ErrorOptions) {
    super(message, options);

    this.name = 'GeolocationError';
  }
}

class GeolocationUndefinedError extends Error {
  code: number;

  constructor(message: string, code: number) {
    super(message);

    this.name = 'GeolocationUndefinedError';
    this.code = code;
  }
}

const getCurrentPositionPromise = (options: PositionOptions) =>
  new Promise<GeolocationPosition>((resolve, reject) => {
    /** There are some ways to disable navigation features in browser
     *  so that navigator.geolocation object will be undefined
     */
    if (!navigator?.geolocation) {
      reject(
        new GeolocationUndefinedError(
          "Client doesn't support getting geolocation",
          GEOLOCATION_PERMISSION_DENIED_ERROR_CODE,
        ),
      );
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });

// options used in mapboxgl-js geolocate control
const positionOptions = { enableHighAccuracy: false, timeout: 60000 };

const InitialLoadGeolocationControl = () => {
  const glMap = useGlMap();
  const history = useHistory();
  const isMobile = useMobile();
  const dispatch = useDispatch();

  const [showingErrorModal, setShowingErrorModal] = useState(false);

  const previousMap = usePrevious(glMap);
  useEffect(() => {
    if (!glMap) {
      return;
    }

    const params = new URLSearchParams(history.location.search);
    const value = params.get(LOCATION_SEARCH_PARAM_NAME);

    if (glMap !== previousMap && !value) {
      // is required to subscribe on move after initial render
      requestAnimationFrame(() => {
        let mapTouched = false;
        glMap.once('move', () => {
          mapTouched = true;
        });

        getCurrentPositionPromise(positionOptions)
          .then(({ coords: { longitude, latitude } }) => {
            if (!mapTouched) {
              glMap.setZoom(INITIAL_GEOLOCATION_MAP_ZOOM);
              glMap.setCenter([longitude, latitude]);
            }
          })
          // all expected geolocation errors will be handled and shown to user
          .catch((err) =>
            setShowingErrorModal(
              err.code === GEOLOCATION_PERMISSION_DENIED_ERROR_CODE ||
                err.code === GEOLOCATION_POSITION_UNAVAILABLE_ERROR_CODE ||
                err.code === GEOLOCATION_TIMEOUT_ERROR_CODE,
            ),
          );
      });
    }
  });

  useEffect(() => {
    getCurrentPositionPromise(positionOptions)
      .then(({ coords: { longitude, latitude } }) => {
        dispatch(
          geolocationChanged({
            latitude,
            longitude,
          }),
        );
      })
      .catch((err) => {
        // Unexpected geolocation errors should be logged to Sentry
        if (
          err.code !== GEOLOCATION_PERMISSION_DENIED_ERROR_CODE &&
          err.code !== GEOLOCATION_POSITION_UNAVAILABLE_ERROR_CODE &&
          err.code !== GEOLOCATION_TIMEOUT_ERROR_CODE
        ) {
          throw new GeolocationError(err.message, {
            cause: err.code,
          });
        }
      });
  }, [dispatch]);

  const handleCloseErrorModal = useCallback(
    () => setShowingErrorModal(false),
    [],
  );

  return showingErrorModal ? (
    <Overlay
      className="initial-load-geolocation-control__error-overlay"
      dark={true}
      position={isMobile ? 'bottom' : 'center'}
      actionable={true}
    >
      <div className="initial-load-geolocation-control__error-modal">
        <CloseButton
          className="initial-load-geolocation-control__error-modal-close-button"
          onClick={handleCloseErrorModal}
        />
        <Icon
          className="initial-load-geolocation-control__error-icon"
          name="question-marker"
        />
        <h2 className="initial-load-geolocation-control__error-title">
          We couldn’t detect your location
        </h2>
        <span className="initial-load-geolocation-control__error-description">
          <p>Please, allow the browser to detect</p>
          <p>your location in settings and make sure</p>
          <p>that it supports geolocation features</p>
        </span>
        <Button
          className="initial-load-geolocation-control__continue-editing-button"
          appearance={appearances.PRIMARY}
          onClick={handleCloseErrorModal}
        >
          Continue editing
        </Button>
      </div>
    </Overlay>
  ) : null;
};

export default InitialLoadGeolocationControl;
